// src/api/pokeApi.js
import { getCachedSprite, batchLoadSprites } from '../utils/spriteCache';

const API_BASE = 'https://pokeapi.co/api/v2';

/**
 * Fetch the full list of Pokémon names + IDs.
 * Returns: Array<{ name: string; id: number; }>
 */
export async function fetchAllPokemon() {
  const res = await fetch(`${API_BASE}/pokemon?limit=10000`);
  if (!res.ok) throw new Error('Failed to fetch Pokémon list');
  const json = await res.json();
  return json.results.map(p => {
    // e.g. p.url = "https://pokeapi.co/api/v2/pokemon/25/"
    const parts = p.url.split('/').filter(Boolean);
    const id = Number(parts[parts.length - 1]);
    return { name: p.name, id };
  });
}

/**
 * Fetch just the names of all Pokémon.
 * Returns: Array<string>
 */
export async function fetchAllPokemonNames() {
  const all = await fetchAllPokemon();
  return all.map(p => p.name);
}

/**
 * Fetch detailed data for one Pokémon by name or ID.
 * Returns an object containing its id, name, stats, types,
 * sprite URL, available abilities, and move list.
 */
export async function fetchPokemonDetails(identifier) {
  const res = await fetch(`${API_BASE}/pokemon/${identifier.toString().toLowerCase()}`);
  if (!res.ok) throw new Error(`No data for Pokémon "${identifier}"`);
  const data = await res.json();
  return {
    id: data.id,
    name: data.name,
    stats: data.stats.reduce((acc, { stat, base_stat }) => {
      let key;
      switch (stat.name) {
        case 'special-attack': key = 'Sp. Attack'; break;
        case 'special-defense': key = 'Sp. Defense'; break;
        default:
          key = stat.name.charAt(0).toUpperCase() + stat.name.slice(1);
      }
      acc[key] = base_stat;
      return acc;
    }, /** @type {Record<string, number>} */({})),
    types: data.types.map(t =>
      t.type.name.charAt(0).toUpperCase() + t.type.name.slice(1)
    ),
    spriteUrl: data.sprites.front_default || `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${data.id}.png`,
    abilities: data.abilities.map(a => a.ability.name),
    moves: data.moves.map(m => m.move.name),
  };
}

/**
 * Fetch all ability names.
 * Returns: Array<string>
 */
export async function fetchAllAbilities() {
  const res = await fetch(`${API_BASE}/ability?limit=10000`);
  if (!res.ok) throw new Error('Failed to fetch abilities');
  const json = await res.json();
  return json.results.map(a => a.name);
}

/**
 * Fetch all item names.
 * Returns: Array<string>
 */
export async function fetchAllItems() {
  const res = await fetch(`${API_BASE}/item?limit=10000`);
  if (!res.ok) throw new Error('Failed to fetch items');
  const json = await res.json();
  return json.results.map(i => i.name);
}

/**
 * Fetch all types (covers both Pokémon types and move types).
 * Returns: Array<string> with capitalized names.
 */
export async function fetchAllTypes() {
  const res = await fetch(`${API_BASE}/type`);
  if (!res.ok) throw new Error('Failed to fetch types');
  const json = await res.json();
  return json.results
    .filter(t => t.name !== 'unknown' && t.name !== 'shadow')
    .map(t => t.name.charAt(0).toUpperCase() + t.name.slice(1));
}

// Aliases so you can clearly distinguish in your UI:
export const fetchAllPokemonTypes = fetchAllTypes;
export const fetchAllMoveTypes   = fetchAllTypes;

/**
 * Fetch all nature names.
 * Returns: Array<string>
 */
export async function fetchAllNatures() {
  const res = await fetch(`${API_BASE}/nature?limit=10000`);
  if (!res.ok) throw new Error('Failed to fetch natures');
  const json = await res.json();
  return json.results.map(n =>
    n.name.charAt(0).toUpperCase() + n.name.slice(1)
  );
}

/**
 * Helper function to get a Pokemon ID from its name.
 * Used to generate sprite/image URLs or fallback IDs for variants.
 */
export const getPokemonIdFromName = (name) => {
  if (!name) return 25; // Default to Pikachu

  const stripped = name.replace(/-mask$/i, '');
  const cleanName = stripped.split(/[-([]/)[0].trim();

  const pokemonIds = {
    // Add canonical mappings here if you want strict control
    // Example: 'charizard': 6,
  };

  const formImages = {
    // Add custom forms here if you have unique image URL logic
    // Example: 'giratina-origin': 492,
  };

  const formHandling = {
    // Handle common aliases or multiple forms
    // Example: 'zygarde-50': 718,
  };

  if (formImages[name]) return formImages[name];
  if (formHandling[name]) return formHandling[name];
  if (pokemonIds[cleanName]) return pokemonIds[cleanName];

  for (const [pokeName, id] of Object.entries(pokemonIds)) {
    if (pokeName.toLowerCase() === cleanName.toLowerCase()) return id;
  }

  return generateFallbackId(cleanName);
};

// Generate a fallback ID for Pokémon not in our dictionary
const generateFallbackId = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = ((hash << 5) - hash) + name.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash % 1025) + 1; // Up to ~Gen 9
};

// The sprite caching service is imported at the top of the file

// Improved getCorrectPokemonSpriteUrl with caching
export async function getPokemonSpriteUrl(name) {
  if (!name) {
    return 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/25.png'; // fallback to Pikachu
  }

  // Handle case where names is an array
  if (Array.isArray(name)) {
    // Use the batch loading function for better performance
    return batchLoadSprites(
      name.map(pokemonItem => {
        if (typeof pokemonItem === 'string') {
          return pokemonItem;
        } else if (typeof pokemonItem === 'object' && pokemonItem !== null) {
          return pokemonItem.slug || pokemonItem.name || '';
        }
        return '';
      }),
      // Pass the single sprite fetcher as the fetch function
      (pokemonName) => getPokemonSpriteUrl(pokemonName)
    );
  }

  // For single Pokémon, use the cached getter with a custom fetch function
  return getCachedSprite(name, async (nameToFetch) => {
    // Ensure name is a string
    const nameStr = String(nameToFetch);

    // Slugify the name properly
    let slugName = nameStr
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');

    // Special case: Pokémon that have "Incarnate" as their default
    const specialIncarnateForms = ['landorus', 'thundurus', 'tornadus', 'enamorus'];
    if (specialIncarnateForms.includes(slugName)) {
      slugName = `${slugName}-incarnate`;
    }

    // Special case: Ogerpon masks (they should have "-mask" on their form)
    if (slugName.startsWith('ogerpon') && !slugName.includes('mask')) {
      slugName = `${slugName}-mask`;
    }

    // Try progressively simpler slugs if needed
    const tryFetchProgressively = async (slug) => {
      const parts = slug.split('-');
      while (parts.length > 0) {
        const attemptSlug = parts.join('-');
        try {
          const details = await fetchPokemonDetails(attemptSlug);
          if (details?.spriteUrl) {
            return details.spriteUrl;
          }
        } catch {
          // Silent failure - we'll try simpler forms
        }
        parts.pop(); // remove last dash part and retry
      }
      return null;
    };

    // First try to fetch normally
    let spriteUrl = await tryFetchProgressively(slugName);

    // Fallback: use consistent raw sprite URL (not official-artwork)
    if (!spriteUrl) {
      const fallbackId = getPokemonIdFromName(nameStr);
      spriteUrl = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${fallbackId}.png`;
    }

    return spriteUrl;
  });
}

// Normalize names progressively (for sprite lookup only)
export const normalizePokemonName = (name) => {
  if (!name) return '';

  let normalized = name.trim().toLowerCase()
    .replace(/\([^)]+\)/g, '')   // Remove things in parentheses
    .replace(/\s+/g, '-')        // Replace spaces with dashes
    .replace(/[^a-z0-9-]/g, ''); // Remove non-alphanumeric except dashes

  // Special form mappings
  const forceFormMap = {
    'tornadus': 'tornadus-incarnate',
    'thundurus': 'thundurus-incarnate',
    'landorus': 'landorus-incarnate',
    'enamorus': 'enamorus-incarnate',
    'shaymin': 'shaymin-land',
    'urshifu-': 'urshifu-rapid-strike',
    'giratina': 'giratina-altered',
    'deoxys': 'deoxys-normal'
  };

  return forceFormMap[normalized] || normalized;
};

